import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useDispatch, useSelector } from "react-redux";
import { getCard } from "./CardRegistry";
import RccHeader from "./RccHeader";
import RccNavigationBar from "./RccNavigationBar";
import RccTeamFilterBar from "./RccTeamFilterBar";
import { appService } from "../App/app.service";
import useRccDrilldowns from "./drilldowns/useRccDrilldowns";
import RccDrilldownModals from "./drilldowns/RccDrilldownModals";
import VisibilityGate from "./VisibilityGate";
import {
  fetchDefaultTeamFilter,
  fetchFirstLevelTeamNames,
  fetchRccTeamFilterScope,
} from "./rccUtils";
import { rccColors } from "./colors";

// Grid column counts per breakpoint
const GRID_COLUMNS_MOBILE = 1;
const GRID_COLUMNS_TABLET = 2;
const GRID_COLUMNS_LAPTOP = 4;

const useStyles = makeStyles((theme) => ({
  container: {
    // Root container: handles viewport width and centering
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    paddingBottom: theme.spacing(2),
    position: "relative",
    boxSizing: "border-box",
  },
  headerContainer: {
    // Mobile (xs) padding
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),

    // Tablet (sm) padding
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },

    // Laptop (md) padding
    [theme.breakpoints.up("md")]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },

    // Desktop (lg) padding -> Go full width only on large screens
    [theme.breakpoints.up("lg")]: {
      paddingLeft: (props) => (props.isHome ? "3%" : theme.spacing(0)),
      paddingRight: (props) => (props.isHome ? "3%" : theme.spacing(0)),
    },

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "1.5rem",
    alignSelf: "stretch",
  },
  gridContainer: {
    // Grid layout only - width inherited from container
    display: "grid",
    gap: theme.spacing(2),
    minWidth: "auto",
    overflow: "visible",
    alignItems: "start",
    boxSizing: "border-box",

    // Mobile (xs) padding
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    // 1. Mobile View (xs) -> 1 Column
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: `repeat(${GRID_COLUMNS_MOBILE}, 1fr)`,
    },

    // 2. Tablet View (sm only) -> 2 Columns
    [theme.breakpoints.only("sm")]: {
      gridTemplateColumns: `repeat(${GRID_COLUMNS_TABLET}, 1fr)`,
      gridAutoRows: "153px",
      alignItems: "stretch",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },

    // 3. Laptop View (md+) -> 4 Columns
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: `repeat(${GRID_COLUMNS_LAPTOP}, 1fr)`,
      gridAutoRows: "153px",
      alignItems: "stretch",
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },

    // 4. Desktop View (lg+) -> Remove padding
    [theme.breakpoints.up("lg")]: {
      paddingLeft: (props) => (props.isHome ? "3%" : theme.spacing(0)),
      paddingRight: (props) => (props.isHome ? "3%" : theme.spacing(0)),
    },
  },
  gridItem: {
    height: "100%",
    display: "flex",
    "& > *": {
      width: "100%",
      height: "100%",
    },
  },
  headerTopRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    flexWrap: "wrap",
    gap: "16px",
  },
  errorBanner: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: rccColors.red[50],
    color: rccColors.red[800],
    border: `1px solid ${rccColors.red[200]}`,
  },
}));

function Index(props) {
  const classes = useStyles({ isHome: props?.isHome });
  const theme = useTheme();
  const dispatch = useDispatch();
  const rccViewAsTeam = useSelector((state) => state.app?.rccViewAsTeam ?? null);
  const rccViewAsTeamError = useSelector((state) => state.app?.rccViewAsTeamError);

  // Define Breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));
  // Covers both md (Laptop) and lg (Desktop) for column logic
  const isLaptopAndUp = useMediaQuery(theme.breakpoints.up("md"));

  const [cardConfig, setCardConfig] = useState([]);
  const [loading, setLoading] = useState(true);

  const drilldowns = useRccDrilldowns();

  const applyTeamFilter = useCallback(
    async (teamName) => {
      setLoading(true);
      dispatch({ type: "set_rcc_view_as_team", error: null });
      try {
        const resp = await appService.getRccConfig(teamName ? { team: teamName } : {});
        if (!resp.success) {
          dispatch({
            type: "set_rcc_view_as_team",
            error: resp.message || "Failed to load RCC configuration",
          });
          return;
        }
        dispatch({
          type: "set_rcc_view_as_team",
          team: teamName || null,
          error: null,
        });
        dispatch({
          type: "set_rcc_layout_context",
          showReps: Boolean(resp?.data?.showReps),
        });
        if (resp?.data?.featureFlags) {
          dispatch({ type: "set_rcc_feature_flags", featureFlags: resp.data.featureFlags });
        }
        dispatch({ type: "clear_rcc_bootstrap_data" });
        await fetchFirstLevelTeamNames();
        await fetchDefaultTeamFilter();
        setCardConfig(Array.isArray(resp?.data?.rccLayout) ? resp.data.rccLayout : []);
      } catch (err) {
        const msg =
          err && typeof err === "object" && err.message
            ? err.message
            : String(err || "Failed to load RCC configuration");
        dispatch({ type: "set_rcc_view_as_team", error: msg });
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  // Determine Max Span allowed
  let VIEW_TOTAL_COLUMNS = 1;
  if (isMobile) VIEW_TOTAL_COLUMNS = GRID_COLUMNS_MOBILE;
  else if (isTablet) VIEW_TOTAL_COLUMNS = GRID_COLUMNS_TABLET;
  else if (isLaptopAndUp) VIEW_TOTAL_COLUMNS = GRID_COLUMNS_LAPTOP;

  useEffect(() => {
    // Preload team filter scope (eligible + descendant teams), bootstrap filters, and card config.
    fetchRccTeamFilterScope();
    fetchFirstLevelTeamNames();
    fetchDefaultTeamFilter();

    appService.getRccConfig().then(
      (resp) => {
        if (resp.success) {
          dispatch({
            type: "set_rcc_layout_context",
            showReps: Boolean(resp?.data?.showReps),
          });
          if (resp?.data?.featureFlags) {
            dispatch({ type: "set_rcc_feature_flags", featureFlags: resp.data.featureFlags });
          }
          if (Array.isArray(resp?.data?.rccLayout)) {
            setCardConfig(resp.data.rccLayout);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching RCC config:", err);
        setLoading(false);
      },
    );
  }, []);

  // Get the pinned card (Performance Insights) and other cards
  const pinnedConfig = useMemo(
    () => cardConfig.find((config) => config.id === "RCC_Performance_Insights"),
    [cardConfig],
  );
  const otherCardsConfig = useMemo(
    () => cardConfig.filter((config) => config.id !== "RCC_Performance_Insights"),
    [cardConfig],
  );
  // Precompute stable drilldown handlers once per config set to avoid
  // passing new function references to every card on each parent render.
  const drilldownHandlers = useMemo(() => {
    const handlers = {};
    otherCardsConfig.forEach((config) => {
      handlers[config.id] = drilldowns.getDrilldownHandler(config.id);
    });
    return handlers;
  }, [otherCardsConfig, drilldowns]);

  // Calculate grid positioning for pinned card
  const getPinnedStyle = (config) => {
    if (!config) return {};
    const style = {};

    if (isMobile) {
      // Mobile: Order 4
      style.order = 4;
      style.gridColumn = "span 1";
    } else if (isTablet) {
      // Tablet (sm): Pinned Right (Col 2)
      style.gridColumn = "2 / 3";
      style.gridRow = "1 / span 3";
    } else if (isLaptopAndUp) {
      // Laptop (md+): Pinned Right (Col 4)
      style.gridColumn = "4 / 5";
      if (config.rowSpan) {
        style.gridRow = `1 / span ${config.rowSpan}`;
      }
    }
    return style;
  };

  const getOtherCardStyle = (config, index) => {
    const style = {};

    if (isMobile) {
      // Mobile ordering
      if (index < 3) {
        style.order = index + 1;
      } else {
        style.order = index + 2;
      }
      style.gridColumn = "span 1";
    } else {
      // Tablet & Laptop Logic
      const requestedSpan = config.columnSpan || 1;
      const actualSpan = Math.min(requestedSpan, VIEW_TOTAL_COLUMNS);

      style.gridColumn = `span ${actualSpan}`;
      style.gridRow = `span ${config.rowSpan || 1}`;
    }

    return style;
  };

  const renderCardFallback = (title = "Card") => (
    <Box
      style={{
        height: "100%",
        minHeight: "120px",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        padding: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#FFFFFF",
        color: "#616161",
      }}
    >
      Loading {title}...
    </Box>
  );

  const renderCard = (config, extraProps = {}) => {
    const CardComponent = getCard(config.id);
    if (!CardComponent) return null;

    return (
      // Each card chunk is lazy-loaded independently; fallback keeps the grid stable
      // while the card module downloads and initializes.
      <Suspense fallback={renderCardFallback(config.title)}>
        <CardComponent
          title={config.title}
          redirectToDashboard={config.redirectToDashboard}
          {...extraProps}
        />
      </Suspense>
    );
  };

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Box className={classes.headerTopRow}>
          <RccHeader />
          <RccTeamFilterBar onApply={applyTeamFilter} disabled={loading} />
        </Box>
        {rccViewAsTeamError ? (
          <Typography className={classes.errorBanner} variant="body2">
            {rccViewAsTeamError}
          </Typography>
        ) : null}
        <RccNavigationBar dashboardLabel="Revenue Command Center" />
      </Box>

      {/* Grid Container */}
      <div className={classes.gridContainer}>
        {loading ? (
          <Box
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            Loading Configuration...
          </Box>
        ) : (
          <>
            {/* Render pinned card first to establish its position */}
            {pinnedConfig &&
              (() => {
                return (
                  <div
                    key={`${pinnedConfig.id}-${rccViewAsTeam || "all"}`}
                    className={classes.gridItem}
                    style={getPinnedStyle(pinnedConfig)}
                  >
                    {renderCard(pinnedConfig)}
                  </div>
                );
              })()}

            {/* Render other cards - they auto-place and wrap around the pinned card */}
            {otherCardsConfig.map((config, index) => {
              // Mount only first row immediately; defer lower rows until near viewport
              // so card effects/data fetches don't all start at once.
              const shouldMountImmediately = index < VIEW_TOTAL_COLUMNS;
              return (
                <div
                  key={`${config.id}-${rccViewAsTeam || "all"}`}
                  className={classes.gridItem}
                  style={getOtherCardStyle(config, index)}
                >
                  <VisibilityGate immediate={shouldMountImmediately}>
                    {renderCard(config, {
                      onDrilldown: drilldownHandlers[config.id],
                    })}
                  </VisibilityGate>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Drilldown modals */}
      <RccDrilldownModals {...drilldowns} />
    </Box>
  );
}

export default Index;
